import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FaEye,
  FaEyeSlash,
  FaTimes,
  FaUserPlus,
} from "react-icons/fa";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";

import "./AddEditUser.css";

const INITIAL_FORM = {
  name: "",
  mobile: "",
  email: "",
  password: "",
  status: "ACTIVE",
};

const AddEditUser = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const api = useMemo(
    () => new API({tokenKey: "platformAccessToken"}),
    []
  );

  const [formData, setFormData] =
    useState(INITIAL_FORM);

  const [errors, setErrors] =
    useState({});

  const [loading, setLoading] =
    useState(false);

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const isEdit = Boolean(user?._id);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (user) {
      setFormData({
        name:
          user?.name ||
          user?.fullName ||
          "",

        mobile:
          user?.mobile ||
          user?.phone ||
          "",

        email:
          user?.email || "",

        password: "",

        status: String(
          user?.status || "ACTIVE"
        ).toUpperCase(),
      });
    } else {
      setFormData({
        ...INITIAL_FORM,
      });
    }

    setErrors({});
    setLoading(false);
    setShowPassword(false);
  }, [open, user]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [open]);

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    let nextValue = value;

    if (name === "mobile") {
      nextValue = value
        .replace(/\D/g, "")
        .slice(0, 10);
    }

    if (name === "email") {
      nextValue =
        value.toLowerCase();
    }

    setFormData((previous) => ({
      ...previous,
      [name]: nextValue,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
      form: "",
    }));
  };

  const validate = () => {
    const nextErrors = {};

    const name = String(
      formData.name || ""
    ).trim();

    const mobile = String(
      formData.mobile || ""
    ).replace(/\D/g, "");

    const email = String(
      formData.email || ""
    )
      .trim()
      .toLowerCase();

    const password = String(
      formData.password || ""
    );

    if (!name) {
      nextErrors.name =
        "Customer name is required.";
    }

    if (
      !/^[6-9]\d{9}$/.test(
        mobile
      )
    ) {
      nextErrors.mobile =
        "Enter a valid 10-digit mobile number.";
    }

    if (
      email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        email
      )
    ) {
      nextErrors.email =
        "Enter a valid email address.";
    }

    if (
      !isEdit &&
      password.length < 8
    ) {
      nextErrors.password =
        "Password must contain at least 8 characters.";
    }

    if (
      isEdit &&
      password &&
      password.length < 8
    ) {
      nextErrors.password =
        "New password must contain at least 8 characters.";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name.trim(),

        mobile:
          formData.mobile.replace(
            /\D/g,
            ""
          ),

        email:
          formData.email
            .trim()
            .toLowerCase() ||
          null,

        status:
          formData.status,
      };

      if (
        !isEdit ||
        formData.password
      ) {
        payload.password =
          formData.password;
      }

      const response = isEdit
        ? await api.put(
            config.updateUser(
              user._id
            ),
            payload
          )
        : await api.post(
            config.createUser,
            payload
          );

      if (
        response?.status !==
        "success"
      ) {
        throw new Error(
          response?.message ||
            "Unable to save customer."
        );
      }

      await onSuccess?.(
        response?.data
      );
    } catch (error) {
      const message =
        error?.response?.data
          ?.message ||
        error?.message ||
        "Unable to save customer.";

      setErrors((previous) => ({
        ...previous,
        form: message,
      }));
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className="customer-modal-root"
      role="presentation"
    >
      <button
        type="button"
        className="customer-modal-backdrop"
        onClick={onClose}
        aria-label="Close customer modal"
      />

      <section
        className="customer-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-modal-title"
      >
        <header className="customer-modal-header">
          <div>
            <span className="customer-modal-header-icon">
              <FaUserPlus />
            </span>

            <div>
              <small>
                CUSTOMER MANAGEMENT
              </small>

              <h2 id="customer-modal-title">
                {isEdit
                  ? "Edit Customer"
                  : "Add Customer"}
              </h2>
            </div>
          </div>

          <button
            type="button"
            className="customer-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <form
          className="customer-modal-form"
          onSubmit={handleSubmit}
        >
          <div className="customer-modal-body">
            {errors.form ? (
              <div className="customer-modal-error">
                {errors.form}
              </div>
            ) : null}

            <div className="customer-modal-grid">
              <div className="customer-modal-field">
                <label htmlFor="customer-name">
                  Customer Name *
                </label>

                <input
                  id="customer-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter customer name"
                  disabled={loading}
                  maxLength={100}
                />

                {errors.name ? (
                  <small>
                    {errors.name}
                  </small>
                ) : null}
              </div>

              <div className="customer-modal-field">
                <label htmlFor="customer-mobile">
                  Mobile Number *
                </label>

                <input
                  id="customer-mobile"
                  type="tel"
                  name="mobile"
                  value={
                    formData.mobile
                  }
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  disabled={loading}
                  inputMode="numeric"
                  maxLength={10}
                />

                {errors.mobile ? (
                  <small>
                    {errors.mobile}
                  </small>
                ) : null}
              </div>

              <div className="customer-modal-field">
                <label htmlFor="customer-email">
                  Email Address
                </label>

                <input
                  id="customer-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  disabled={loading}
                />

                {errors.email ? (
                  <small>
                    {errors.email}
                  </small>
                ) : null}
              </div>

              <div className="customer-modal-field">
                <label htmlFor="customer-status">
                  Status *
                </label>

                <select
                  id="customer-status"
                  name="status"
                  value={
                    formData.status
                  }
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="ACTIVE">
                    Active
                  </option>

                  <option value="INACTIVE">
                    Inactive
                  </option>

                  <option value="BLOCKED">
                    Blocked
                  </option>
                </select>
              </div>

              <div className="customer-modal-field customer-modal-full-field">
                <label htmlFor="customer-password">
                  {isEdit
                    ? "New Password"
                    : "Password *"}
                </label>

                <div className="customer-password-wrapper">
                  <input
                    id="customer-password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    name="password"
                    value={
                      formData.password
                    }
                    onChange={handleChange}
                    placeholder={
                      isEdit
                        ? "Leave blank to keep current password"
                        : "Minimum 8 characters"
                    }
                    disabled={loading}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (previous) =>
                          !previous
                      )
                    }
                    disabled={loading}
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <FaEyeSlash />
                    ) : (
                      <FaEye />
                    )}
                  </button>
                </div>

                {errors.password ? (
                  <small>
                    {errors.password}
                  </small>
                ) : isEdit ? (
                  <span className="customer-modal-help">
                    Leave blank when the password should not change.
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <footer className="customer-modal-footer">
            <button
              type="button"
              className="customer-modal-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="customer-modal-save"
              disabled={loading}
            >
              {loading
                ? "Please wait..."
                : isEdit
                  ? "Update Customer"
                  : "Create Customer"}
            </button>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default AddEditUser;